import React, { Fragment, ReactNode } from 'react';
import toast from 'react-hot-toast';
import {
  useCover,
  useHasEnteredCoverDetails,
  useHasQuestions,
  useIsCreatingWorksheet,
  useOptions,
  useQuestions,
  worksheetActions,
} from '@src/stores/worksheet.store';
import { pdf } from '@react-pdf/renderer';
import * as api from '@src/utils/mmc-api/api.js';
import WorksheetPDF from './steps/Document';
import { downloadFile } from '@src/utils/file';
import { useComponentSwitcher } from '../ComponentSwitcher';
import { Spinner } from '../Spinner';
import * as styles from './maker.module.css';
import { steps } from './Maker';
import { sortQuestionsByDifficulty, renumberQuestions } from './Maker.helpers';

type LayoutProps = {
  children: ReactNode;
  actions?: ReactNode;
};

const errorMessages = {
  401: 'Please login to create a worksheet.',
  403: 'Question bank subscription required.',
  404: 'One or more questions could not be found.',
  429: 'Too many requests. Please try again later.',
  500: 'Server error. Our team has been notified.',
  default: 'Failed to create worksheet. Please try again.',
};

const Layout = ({ children, actions }: LayoutProps) => {
  const switcher = useComponentSwitcher();
  const questions = useQuestions();
  const hasQuestions = useHasQuestions();
  const options = useOptions();
  const cover = useCover();
  const coverDetails = useHasEnteredCoverDetails();
  const creatingWorksheet = useIsCreatingWorksheet();

  const handleCreate = async () => {
    worksheetActions.setLoading(true);

    try {
      // API call to create worksheet
      const ws = await api.createWorksheet({
        slugs: questions.map((q) => q.slug),
        options,
      });

      if (ws?.statusCode) {
        toast.error(errorMessages[ws.statusCode] || errorMessages.default);
        return;
      }

      // Sort questions by difficulty if enabled
      const sortedQuestionPDF = options.orderByDifficulty
        ? renumberQuestions(sortQuestionsByDifficulty(ws.questionPDF))
        : ws.questionPDF;

      // Generate and download worksheet PDF
      const blob = await pdf(
        <WorksheetPDF
          type="worksheet"
          questions={sortedQuestionPDF}
          {...(coverDetails && { cover })}
          incrementQuestions={options.incrementQuestions}
          hasQrCode={options.questionMsQR || options.questionWrittenMsQR}
          hasSubtopics={options.subtopic}
        />,
      ).toBlob();
      downloadFile(blob, 'worksheet.pdf');

      // Generate and download mark scheme PDF if requested
      if (options.markSchemePDF) {
        // Sort mark scheme questions by difficulty if enabled
        const sortedMarkSchemePDF = options.orderByDifficulty
          ? renumberQuestions(sortQuestionsByDifficulty(ws.markSchemePDF))
          : ws.markSchemePDF;

        const markSchemeBlob = await pdf(
          <WorksheetPDF
            cover={cover}
            type="markScheme"
            questions={sortedMarkSchemePDF}
            {...(coverDetails && { cover })}
            incrementQuestions={options.incrementQuestions}
            hasQrCode={options.writtenMsQR}
          />,
        ).toBlob();
        downloadFile(markSchemeBlob, 'markScheme.pdf');
      }

      toast.success('Worksheet created! Check downloads.');
    } catch (error) {
      console.error('Worksheet creation error:', error);
      toast.error('Issue creating worksheet.');
    } finally {
      worksheetActions.setLoading(false);
    }
  };

  const currentStep = steps.findIndex((step) => step.step === switcher.current.name);

  return (
    <Fragment>
      <div className={styles.body}>
        {children}
        <div className={styles.step}>
          Step {currentStep + 1} of {steps.length}
          <div className={styles.navigation}>
            <button onClick={switcher.actions.prev}>
              &lt; <span>Prev</span>
            </button>
            <button onClick={switcher.actions.next}>
              <span>Next</span> &gt;
            </button>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <button
          disabled={creatingWorksheet || !hasQuestions}
          className="button"
          onClick={handleCreate}
        >
          {creatingWorksheet ? <Spinner size={18} /> : 'Create'}
        </button>
        <button
          className="button"
          onClick={() => {
            worksheetActions.reset();
            worksheetActions.setAllSelected(false);
            switcher.actions.navigate('questions');
          }}
        >
          Reset
        </button>
        {actions}
      </div>
    </Fragment>
  );
};

export default Layout;
